import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edge, SafeAreaView } from 'react-native-safe-area-context';

import { Button, Modal, ModalDialogue, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagCancelModal = (props: Props) => {
  const { visible, isCancelingGroupOrder, onClose, onConfirm } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isCancelingGroupOrder) return;

    onClose();
  }, [isCancelingGroupOrder, onClose]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible={visible}
      size="small"
      headline={formatMessage(messages.title)}
      body={formatMessage(messages.body)}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleClose}
    >
      <Modal.Footer withoutTopBorder>
        <SafeAreaView style={styles.footer} edges={SAFE_AREA_EDGES}>
          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="secondary"
              disabled={isCancelingGroupOrder}
              onPress={handleClose}
            >
              <FormattedMessage {...messages.goBackCta} />
            </Button>
          </View>

          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="caution"
              isLoading={isCancelingGroupOrder}
              onPress={onConfirm}
            >
              <FormattedMessage {...messages.cancelGroupOrderCta} />
            </Button>
          </View>
        </SafeAreaView>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edge[] = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Cancel group order confirmation',
    description: 'Bag | Group order | Cancel modal | Accessibility label',
  },
  title: {
    defaultMessage: 'Cancel group order?',
    description: 'Bag | Group order | Cancel modal | Title',
  },
  body: {
    defaultMessage:
      'This will cancel the order for everyone in the group. They’ll all be notified.',
    description: 'Bag | Group order | Cancel modal | Body',
  },
  goBackCta: {
    defaultMessage: 'Go back',
    description: 'Bag | Group order | Cancel modal | Go back CTA',
  },
  cancelGroupOrderCta: {
    defaultMessage: 'Cancel group order',
    description: 'Bag | Group order | Cancel modal | Confirm CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  ctaContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  isCancelingGroupOrder: boolean;
  onClose: () => void;
  onConfirm: () => void;
};
